/**
 * A real gap, or one nobody has looked at enough.
 *
 * A product is linked to a complaint only when a seat reads its page and the
 * page says it solves it. So "nobody addresses it" on a board where few pages
 * were read mostly means "not checked yet". The server decides which it is
 * (`gap_checked` in reports.py) and says how many pages were read; this turns
 * that into words. A row from before the check existed counts as confirmed, so
 * an old board reads the way it did.
 */
import type { BoardroomPainRow } from '../../../types';

export function isConfirmedGap(r: BoardroomPainRow): boolean {
  return r.gap && r.gap_confirmed !== false;
}

export function readWords(f: { read: number; of: number }): string {
  return f.of === 0 ? 'no products to read' : `${f.read} of ${f.of} product pages read`;
}

/** The word in the Gap column, and what it sorts on. */
export function gapWord(r: BoardroomPainRow): string {
  if (!r.gap) return '';
  return isConfirmedGap(r) ? 'gap' : 'unconfirmed';
}

export function gapExplain(r: BoardroomPainRow): string {
  const read = r.field_read ? ` (${readWords(r.field_read)})` : '';
  return isConfirmedGap(r)
    ? `Nobody on the board claims to solve this${read}.`
    : `No product is linked to this yet, but too few pages were read to know${read}.`;
}

export function gapTone(r: BoardroomPainRow): string {
  return isConfirmedGap(r)
    ? 'bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300'
    : 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300';
}

export function gapTotals(t: { gaps: number; confirmed_gaps?: number }): string {
  const confirmed = t.confirmed_gaps ?? t.gaps;
  const open = t.gaps - confirmed;
  const head = `${confirmed} gap${confirmed === 1 ? '' : 's'}`;
  return open > 0 ? `${head} · ${open} unconfirmed` : head;
}
